import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import ArrowUpRightSvg from "@redotech/redo-icons/arrow-up-right_filled.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { isRedoUser } from "@redotech/redo-model/checkouts/trusted-users";
import {
  ConversationTagWithId,
  ExpandedConversation,
  getConversationStatus,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { AIInformation } from "@redotech/redo-model/conversation/conversation-schema-db-parser";
import { UpdateConversationBody } from "@redotech/redo-model/updateconversationbody";
import { GetUser as User } from "@redotech/redo-model/user";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { FiveStarRating } from "@redotech/redo-web/five-star-rating";
import { Flex } from "@redotech/redo-web/flex";
import { ImageLightbox } from "@redotech/redo-web/image-lightbox";
import { ExternalLink } from "@redotech/redo-web/link";
import { RedoSupportChannelBadge } from "@redotech/redo-web/support/redo-support-channel-badge";
import { RedoSupportStatusBadge } from "@redotech/redo-web/support/redo-support-status-badge";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { updateConversation } from "../client/conversations";
import { ConversationCloseValidationContext } from "../context/conversation-close-validation-context";
import { ConversationTagInput } from "../conversation-tags/conversation-tag-input";
import { AssigneeDetails } from "./assignee-details";
import * as conversationDetailsPanelCss from "./conversation-details-panel.module.css";

export const ConversationDetailsOverview = memo(
  function ConversationDetailsOverview({
    conversation,
    setConversationAssignee,
  }: {
    conversation: MinimalConversation | ExpandedConversation;
    setConversationAssignee: (assignee: any) => void;
  }) {
    const client = useRequiredContext(RedoMerchantClientContext);
    const user = useContext(UserContext);
    const { revalidate: revalidateCloseValidation } = useContext(
      ConversationCloseValidationContext,
    );

    const [imageLightboxOpen, setImageLightboxOpen] = useState(false);
    const [conversationTagsToShow, setConversationTagsToShow] = useState<
      ConversationTagWithId[]
    >([]);

    const redoMerchantRpcClient = useRequiredContext(
      RedoMerchantRpcClientContext,
    );

    const tagIds = conversation.tagIds?.map((tag) => tag.tagId) || [];
    const conversationTags = useLoad(async () => {
      if (tagIds.length === 0) {
        return [];
      }
      return await redoMerchantRpcClient.getConversationTagsById({ tagIds });
    }, [redoMerchantRpcClient, JSON.stringify(tagIds.sort())]);

    useEffect(() => {
      if (conversationTags.value) {
        const tagsWithId: ConversationTagWithId[] = conversationTags.value.map(
          (tag) => ({
            tagId: tag._id,
            name: tag.name,
            pillTheme: tag.pillTheme,
            source: tag.source,
            tagGroupId: tag.tagGroupId,
          }),
        );
        setConversationTagsToShow(tagsWithId);
      } else {
        setConversationTagsToShow(conversation.tagIds || []);
      }
    }, [conversationTags.value, conversation.tagIds]);

    async function updateConversationTags(
      updatedTags: ConversationTagWithId[],
    ) {
      setConversationTagsToShow(updatedTags);

      const body: UpdateConversationBody = { tags: updatedTags };

      await updateConversation(client, conversation, body);

      await revalidateCloseValidation?.(conversation._id);
    }

    const handleUpdateConversationDetails = async ({
      assignee,
    }: {
      assignee: User | null;
    }) => {
      amplitude.logEvent("update-conversationAssignee", {
        mode: "single",
        conversationIds: [conversation._id],
        channels: [conversation.platform],
        assignee: assignee?._id,
      });
      // Optimistic update - update UI immediately
      const previousAssignee = conversation.assignee;
      setConversationAssignee(assignee);
      await alertOnFailure("Unable to change assignee")(async () => {
        try {
          await updateConversation(client, conversation, {
            assignee: assignee ? assignee._id : null,
          });
        } catch (error) {
          // Revert on failure
          setConversationAssignee(previousAssignee);
          throw error;
        }
      });
    };

    const aiInfo =
      "aiInformation" in conversation
        ? (conversation.aiInformation as AIInformation | undefined)
        : undefined;
    const llmThreadId = aiInfo?.llmThreadId;
    const showLlmThreadButton =
      user && isRedoUser(user.email) && llmThreadId !== undefined;

    return (
      <Flex dir="column" gap="xl">
        <CsatScore conversation={conversation} />
        <Flex gap="md" wrap="wrap">
          <RedoSupportChannelBadge platform={conversation.platform} size="xs" />
          <RedoSupportStatusBadge
            size="xs"
            snoozedUntil={conversation.snoozedUntil}
            status={getConversationStatus(conversation)}
          />
          {showLlmThreadButton && llmThreadId && (
            <Link to={`/support/llm-thread/${llmThreadId}`}>
              <RedoBadge
                color="gray"
                iconTrailing={{ type: "icon", Icon: ArrowUpRightSvg }}
                size="xs"
                text="LLM Thread"
              />
            </Link>
          )}
        </Flex>
        {conversation.instagramCommentThread && (
          <Flex>
            <Flex>
              {conversation.instagramCommentThread?.mediaPreviewUrl && (
                <>
                  <div
                    className={conversationDetailsPanelCss.postPreview}
                    onClick={() => setImageLightboxOpen(true)}
                  >
                    <img
                      alt="Instagram post"
                      src={conversation.instagramCommentThread.mediaPreviewUrl}
                    />
                  </div>
                  <ImageLightbox
                    imageSrc={
                      conversation.instagramCommentThread.mediaPreviewUrl
                    }
                    onClose={() => setImageLightboxOpen(false)}
                    open={imageLightboxOpen}
                  />
                </>
              )}
            </Flex>
            <Flex
              className={conversationDetailsPanelCss.wrappingText}
              dir="column"
            >
              <ExternalLink
                newTab
                showIcon={false}
                url={
                  conversation.instagramCommentThread?.permalink ?? undefined
                }
              >
                <RedoBadge
                  color="gray"
                  iconTrailing={{ type: "icon", Icon: ArrowUpRightSvg }}
                  size="xs"
                  text="See post"
                />
              </ExternalLink>
            </Flex>
          </Flex>
        )}
        {conversation.facebookCommentThread && (
          <Flex>
            <Flex>
              {conversation.facebookCommentThread?.mediaPreviewUrl && (
                <>
                  <div
                    className={conversationDetailsPanelCss.postPreview}
                    onClick={() => setImageLightboxOpen(true)}
                  >
                    <img
                      alt="Facebook post"
                      src={conversation.facebookCommentThread.mediaPreviewUrl}
                    />
                  </div>
                  <ImageLightbox
                    imageSrc={
                      conversation.facebookCommentThread.mediaPreviewUrl
                    }
                    onClose={() => setImageLightboxOpen(false)}
                    open={imageLightboxOpen}
                  />
                </>
              )}
            </Flex>
            <Flex
              className={conversationDetailsPanelCss.wrappingText}
              dir="column"
            >
              <ExternalLink
                newTab
                showIcon={false}
                url={conversation.facebookCommentThread?.permalink ?? undefined}
              >
                <RedoBadge
                  color="gray"
                  iconTrailing={{ type: "icon", Icon: ArrowUpRightSvg }}
                  size="xs"
                  text="See post"
                />
              </ExternalLink>
            </Flex>
          </Flex>
        )}
        <Flex gap="md" wrap="wrap">
          <ConversationTagInput
            currentTags={conversationTagsToShow}
            setCurrentTags={(tags) => updateConversationTags(tags)}
            showAddButtonToEnterInput
          />
        </Flex>
        <AssigneeDetails
          conversation={conversation}
          updateConversationDetails={handleUpdateConversationDetails}
        />
      </Flex>
    );
  },
);

const CsatScore = memo(function CsatScore({
  conversation,
}: {
  conversation: MinimalConversation;
}) {
  const redoMerchantRpcClient = useRequiredContext(
    RedoMerchantRpcClientContext,
  );

  const csatScore = useLoad(async () => {
    const result = await redoMerchantRpcClient.getCsatResponsesForConversation({
      conversationId: conversation._id,
    });
    if (result.length === 0) {
      return undefined;
    }
    const scores = result.map((response) => response.rating);
    const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    return averageScore;
  }, [conversation._id]);

  if (csatScore.value !== undefined) {
    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          CSAT
        </Text>
        <FiveStarRating
          className={conversationDetailsPanelCss.csatScore}
          rating={csatScore.value}
          uniqueId={`${conversation._id}-details`}
        />
      </Flex>
    );
  } else {
    return null;
  }
});
